/**
 * La tabla del portfolio.
 *
 * Usa `densidadPorSeveridad`: la fila crítica respira y la sana va compacta y en
 * gris, así el ojo cae en lo que está roto sin ordenar ni filtrar nada. El color
 * es refuerzo — cada fila lleva además su pill de estado y su porcentaje de
 * activación en texto.
 *
 * El orden base (el que devuelve el tercer click en un encabezado) es por
 * riesgo, no alfabético: en una herramienta de ops lo útil por defecto es lo más
 * roto arriba.
 */
import {
  CLIENT_STATE_LABELS,
  SEGMENT_LABELS,
  formatMetricValue,
  formatRelativeDays,
  type Client,
  type Links,
  type Provenance,
} from '@ops/contracts'
import type { ReactNode } from 'react'

import { useVincular } from '../../lib/vincular'
import {
  DataTable,
  MissingCell,
  ProgressBar,
  ProvenanceBadge,
  StatusPill,
  type Column,
} from '../../ui'
import { SistemasDeOrigen } from './SistemasDeOrigen'
import {
  DIAS_EN_ESTADO_TRABADO,
  DIAS_SIN_CONTACTO_ALERTA,
  diasDesde,
  saludActivacion,
  saludCliente,
  saludEstado,
} from './derivados'

/**
 * Proveniencia de un grupo de columnas de la tabla.
 *
 * La regla 1 pide fuente **y frescura** en cada número, y `Client` no trae
 * `provenance`: la API no manda proveniencia por fila (ver la nota al pie de
 * este archivo). Lo que sí se puede publicar con honestidad es la proveniencia
 * de la carga que alimenta cada grupo de columnas, que es la misma para toda la
 * tabla, y por eso el badge va a nivel de bloque y no por celda.
 */
export interface ProvenienciaDeColumnas {
  /** Qué columnas cubre, en el mismo vocabulario de los encabezados. */
  columnas: string
  /** `null` = la fuente no está declarada, y el badge lo dice así. */
  provenance: Provenance | null
}

export interface PortfolioTablaProps {
  clientes: readonly Client[]
  links: Links['clientes']
  ahora: Date
  verPlata: boolean
  onAbrir: (clientId: string) => void
  /** Contexto y fuentes de la tabla, para el `caption`. */
  caption: string
  /** Fuente y frescura por grupo de columnas. Regla 1. */
  proveniencias?: readonly ProvenienciaDeColumnas[]
  /** Pie de la tabla: totales. */
  footer?: ReactNode
}

export function PortfolioTabla({
  clientes,
  links,
  ahora,
  verPlata,
  onAbrir,
  caption,
  proveniencias = [],
  footer,
}: PortfolioTablaProps) {
  const vincular = useVincular()

  const columnas: Column<Client>[] = [
    {
      key: 'nombre',
      header: 'Cuenta',
      rowHeader: true,
      width: '200px',
      sortValue: (fila) => fila.nombre,
      filterValue: (fila) => `${fila.nombre} ${fila.vertical}`,
      render: (fila) => (
        <span className="flex flex-col items-start gap-1">
          <span className="truncate">{fila.nombre}</span>
          {fila.activacion !== null &&
          fila.activacion.contratados > 0 &&
          fila.activacion.instalados === 0 ? (
            <StatusPill severidad="critical" size="sm">
              {fila.activacion.contratados} contratados, 0 corriendo
            </StatusPill>
          ) : null}
        </span>
      ),
    },
    {
      key: 'vertical',
      header: 'Vertical',
      sortValue: (fila) => fila.vertical,
      render: (fila) => <span className="text-muted-strong">{fila.vertical}</span>,
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '176px',
      sortValue: (fila) => CLIENT_STATE_LABELS[fila.estado],
      render: (fila) => (
        <StatusPill severidad={saludEstado(fila.estado)} size="sm">
          {CLIENT_STATE_LABELS[fila.estado]}
        </StatusPill>
      ),
    },
    {
      key: 'segmento',
      header: 'Segmento',
      sortValue: (fila) => SEGMENT_LABELS[fila.segmento],
      render: (fila) => (
        <span className="text-muted-strong">{SEGMENT_LABELS[fila.segmento]}</span>
      ),
    },
  ]

  if (verPlata) {
    columnas.push({
      key: 'mrr',
      header: 'MRR',
      align: 'right',
      width: '96px',
      sortValue: (fila) => fila.mrr,
      // Regla 2 también acá: para admin un `mrr` en null es una cuenta sin
      // importe cargado, no un cero ni un guion. (Para ops y comercial la
      // columna no existe: el servidor recorta el campo.)
      render: (fila) =>
        fila.mrr === null ? (
          <MissingCell que="MRR" />
        ) : (
          <span className="font-semibold">
            {formatMetricValue(fila.mrr, 'usd')}
          </span>
        ),
    })
  }

  columnas.push(
    {
      key: 'activacion',
      header: 'Instalados / contratados',
      width: '188px',
      sortValue: (fila) => fila.activacion?.pctActivado ?? null,
      render: (fila) => {
        if (fila.activacion === null) {
          return (
            <span className="text-micro text-muted-strong">
              Sin medición de activación
            </span>
          )
        }
        const salud = saludActivacion(fila.activacion)
        return (
          <span className="flex flex-col gap-1">
            <span className="tabular text-meta">
              <b className="font-semibold">{fila.activacion.instalados}</b> de{' '}
              {fila.activacion.contratados}
              {fila.activacion.brecha > 0 ? (
                <span className="ml-1.5 font-semibold text-danger-text">
                  −{fila.activacion.brecha} sin instalar
                </span>
              ) : null}
            </span>
            <ProgressBar
              pct={fila.activacion.pctActivado}
              label={`Activación de ${fila.nombre}`}
              severidad={salud}
              size="sm"
              etiqueta={formatMetricValue(fila.activacion.pctActivado, 'percent')}
            />
          </span>
        )
      },
    },
    {
      key: 'owner',
      header: 'Dueño',
      sortValue: (fila) => fila.owner?.nombre ?? null,
      render: (fila) =>
        fila.owner === null ? (
          <span className="text-micro text-muted-strong">Sin dueño asignado</span>
        ) : (
          fila.owner.nombre
        ),
    },
    {
      key: 'diasEnEstado',
      header: 'Días en estado',
      align: 'right',
      width: '96px',
      sortValue: (fila) => fila.diasEnEstado,
      render: (fila) => (
        <span
          className={
            fila.diasEnEstado >= DIAS_EN_ESTADO_TRABADO
              ? 'font-semibold text-danger-text'
              : undefined
          }
        >
          {formatMetricValue(fila.diasEnEstado, 'count')}
        </span>
      ),
    },
    {
      key: 'ultimoContacto',
      header: 'Último contacto',
      align: 'right',
      width: '120px',
      // Sin contacto registrado ordena al final: `comparar` manda los null abajo.
      sortValue: (fila) => diasDesde(fila.ultimoContacto, ahora),
      render: (fila) => {
        const dias = diasDesde(fila.ultimoContacto, ahora)
        const alerta = dias !== null && dias > DIAS_SIN_CONTACTO_ALERTA
        return (
          <span className={alerta ? 'font-semibold text-warning-text' : undefined}>
            {formatRelativeDays(fila.ultimoContacto, ahora)}
          </span>
        )
      },
    },
    {
      key: 'sistemas',
      header: 'Sistemas de origen',
      width: '232px',
      render: (fila) => (
        <SistemasDeOrigen
          links={links[fila.id]}
          nombre={fila.nombre}
          verPlata={verPlata}
          // La celda es densa pero el botón entra: sin este handler los siete
          // estados "sin vincular" de la tabla mostraban el texto y nada más.
          onVincular={(sistema) => vincular(`${sistema} de ${fila.nombre}`)}
        />
      ),
    },
  )

  return (
    <DataTable
      columns={columnas}
      rows={clientes}
      rowKey={(fila) => fila.id}
      rowSeverity={(fila) => saludCliente(fila, ahora)}
      densidadPorSeveridad
      onRowClick={(fila) => onAbrir(fila.id)}
      emptyMessage="Ninguna cuenta coincide con estos filtros."
      caption={
        <>
          {caption}
          {proveniencias.length > 0 ? (
            <span
              data-testid="portfolio-proveniencia"
              className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              {proveniencias.map((p) => (
                <span key={p.columnas} className="inline-flex items-center gap-1">
                  <span className="text-micro text-muted-strong">
                    {p.columnas}:
                  </span>
                  <ProvenanceBadge
                    provenance={p.provenance}
                    now={ahora}
                    hrefAdmin="/admin"
                    variant="inline"
                  />
                </span>
              ))}
            </span>
          ) : null}
        </>
      }
      footer={footer}
    />
  )
}
